import styled from '@emotion/styled';
import React from 'react';
import Modal from 'react-modal';
import { parseHtml } from '../helpers';

import { StyleLibrary } from '@vm/omega-shared-component-webapp';
const { mqBreakpoints, pxToRem } = StyleLibrary;

type Props = {
  modalContent: string;
  isOpen?: boolean;
  onCtaClick: () => void;
  onClose: () => void;
};

const modalCustomStyles = {
  overlay: {
    position: 'fixed',
    zIndex: '999',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflow: 'auto',
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  content: {
    position: 'relative',
    background: 'white',
    margin: '40px auto',
    inset: 'revert',
    width: '85%',
    maxWidth: pxToRem(600),
    zIndex: 1,
    padding: 0,
    borderRadius: 0,
  },
};

const ModalHeader = styled.div([
  {
    backgroundColor: '#F9F9F9;',
    padding: `${pxToRem(16)} ${pxToRem(24)}`,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'baseline',

    '& p': {
      fontSize: pxToRem(21),
      fontFamily: 'var(--font-header)',
      fontWeight: 'var(--font-weight-regular)',
      paddingBottom: 0,
      flexGrow: 2,
      textAlign: 'center',
    },
  },
]);

const Hr = styled.hr({
  border: '1px solid #E7EAEC',
  margin: 0,
});

const CloseButton = styled.button({
  cursor: 'pointer',
  border: 'none',
  backgroundColor: '#F9F9F9;',
  fontSize: pxToRem(24),
});

const ModalContent = styled.div({
  padding: `${pxToRem(48)} ${pxToRem(32)}`,

  '& h2': {
    color: 'var(--color-primary)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-medium)',
    fontSize: pxToRem(28),
    lineHeight: pxToRem(36),
  },

  '& h3': {
    fontWeight: 'var(--font-weight-medium)',
    fontSize: pxToRem(20),
    lineHeight: pxToRem(28),
    letterSpacing: '-0.25px',
    paddingBottom: pxToRem(24),
  },

  '& button': {
    marginBottom: pxToRem(36),
    fontWeight: 'var(--font-weight-regular)',
  },

  '& p': {
    paddingBottom: pxToRem(24),
    marginBottom: pxToRem(0),
    fontWeight: 'var(--font-weight-lighter)',

    '& strong': {
      fontWeight: 'var(--font-weight-medium)',
    },
  },

  '& a': {
    textDecoration: 'none',
    marginBottom: 'revert',
    whiteSpace: 'nowrap', // prevent breaking to new line for mobile devices
  },
});

export const CtaModal: React.FC<Props> = props => {
  const { onCtaClick, onClose, modalContent, isOpen } = props;

  // as the continue button is parsed from HTML, it't not possible to pass 'onCtaClick' prop directly
  const afterModalOpen = () => {
    const continueBtn = document.getElementById('cta-modal-continue');
    continueBtn.addEventListener('click', e => {
      e.preventDefault();
      onCtaClick();
    });
  };

  return (
    <Modal
      appElement={document.body}
      isOpen={isOpen}
      onAfterOpen={afterModalOpen}
      onRequestClose={onClose}
      style={modalCustomStyles}
      contentLabel='Before you apply'
      aria={{
        describedby: 'full_description',
        modal: 'true',
      }}
      testId='mp-product-cta-modal'
    >
      <div className='sr-only' id='full_description'>
        This is a dialog window which overlays the main content of the page. The modal begins with a heading 2 called "Check your eligibility". Pressing the Close Modal button at the top of the modal will close the modal and bring you back to where you were on the page. Pressing the Continue enquiry
        button will open a new page with the form to fill in. Pressing the Learn about Virgin Startup link will open page.
      </div>
      <ModalHeader>
        <p>Before you apply</p>
        <CloseButton title='Close Modal' id='close' className='cta-modal-close' onClick={onClose}>
          <span className='sr-only'>Close</span>
          <span aria-hidden='true'>×</span>
        </CloseButton>
      </ModalHeader>
      <Hr />
      <ModalContent>{parseHtml(modalContent)}</ModalContent>
    </Modal>
  );
};
